import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RecruitmentApiService } from '../../services/recruitment-api.service';
import { AdminApiService } from '../../services/admin-api.service';
import { CandidateDecision, CandidateDetail, CandidateRoleFit } from '../models/recruitment.models';

@Component({
  selector: 'app-candidate-detail',
  templateUrl: './candidate-detail.component.html',
  styleUrls: ['./candidate-detail.component.css'],
  standalone: false,
})
export class CandidateDetailComponent implements OnInit {
  candidate: CandidateDetail | null = null;
  selectedFit: CandidateRoleFit | null = null;
  loading = true;
  errorMessage = '';
  decision: CandidateDecision = 'UNREVIEWED';
  note = '';
  saving = false;
  readonly decisions: CandidateDecision[] = ['UNREVIEWED', 'MOVE_AHEAD', 'HOLD', 'REJECTED'];

  constructor(
    private route: ActivatedRoute,
    private recruitmentApi: RecruitmentApiService,
    private adminApi: AdminApiService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const candidateId = params.get('candidateId');
      if (candidateId) this.loadCandidate(candidateId);
    });
  }

  loadCandidate(candidateId = this.candidate?.userId || ''): void {
    if (!candidateId) return;
    this.loading = true;
    this.errorMessage = '';
    this.recruitmentApi.getCandidateDetail(candidateId).subscribe({
      next: (candidate) => {
        this.candidate = candidate;
        this.selectedFit = candidate.fits[0] || null;
        this.decision = candidate.decision;
        this.note = candidate.note || '';
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.errorMessage = 'Candidate details could not be loaded.';
      },
    });
  }

  selectFit(fit: CandidateRoleFit): void {
    this.selectedFit = fit;
  }

  saveReview(): void {
    if (!this.candidate || !this.selectedFit) return;
    this.saving = true;
    this.recruitmentApi.updateCandidateDecision({
      benchmarkId: this.selectedFit.benchmarkId,
      candidateId: this.candidate.userId,
      state: this.decision,
      note: this.note.trim(),
    }).subscribe({
      next: () => {
        this.saving = false;
        this.snackBar.open('Candidate review saved', 'Close', { duration: 2200 });
      },
      error: () => {
        this.saving = false;
        this.snackBar.open('Candidate review could not be saved', 'Dismiss', { duration: 2800 });
      },
    });
  }

  viewResume(): void {
    if (!this.candidate?.userId) return;
    this.adminApi.getUserById(this.candidate.userId).subscribe({
      next: (response) => this.openResumeOrNotify(response?.user?.resumeUrl),
      error: () => this.notifyResumeUnavailable(),
    });
  }

  private openResumeOrNotify(resumeUrl: string | undefined): void {
    if (resumeUrl) {
      window.open(resumeUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    this.notifyResumeUnavailable();
  }

  private notifyResumeUnavailable(): void {
    this.snackBar.open('Resume is not available on file.', 'Dismiss', { duration: 3200 });
  }

  initials(): string {
    if (!this.candidate) return '';
    return `${this.candidate.firstName.charAt(0)}${this.candidate.lastName.charAt(0)}`.toUpperCase();
  }

  decisionLabel(decision: CandidateDecision): string {
    return ({ UNREVIEWED: 'Unreviewed', MOVE_AHEAD: 'Move ahead', HOLD: 'Hold', REJECTED: 'Rejected' })[decision];
  }
}
